import Box16 from '@carbon/icons-react/es/box/16';
import { SortOrder } from 'antd/es/table/interface';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ArchivedProject } from 'api/project';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { UserTag } from 'components/atoms/select';
import { COLUMN_HEADER_WIDTH, ColumnHeader } from 'components/atoms/table';
import { ProjectAvatarWithFallback } from 'components/molecules/fallback-avatar/';
import { fullName } from 'utils/full-name';
import { getProjectStatusDetails } from 'utils/get-project-status-details';
import { getRandomColor } from 'utils/get-random-color';
import { renderStatusField } from 'utils/render-status-field';
import { sortByDates } from 'utils/sort-by-dates';
import { sortByText } from 'utils/sort-by-text';
import { MoreActionsMenu } from './more-actions-menu';
import {
  LongText,
  MoreActions,
  ProjectNameWrapper,
  WithAvatarSpace,
} from './styles';

export const archivedProjectsListColumns = [
  {
    titleText: <FormattedMessage id="projects.list.projectName" />,
    key: 'projectName',
    fixed: 'left' as const,
    width: 288,
    ellipsis: true,
    render: (_text, project: ArchivedProject) =>
      project.customer && (
        <WithAvatarSpace>
          <ProjectAvatarWithFallback
            name={project.customer.companyName}
            src={project.customer.companyLogoUrl}
            size="default"
          />
          <ProjectNameWrapper>
            <Box16 color="var(--gray-40)" />
            <LongText
              width={220}
              ellipsis={{
                tooltip: project.projectName,
              }}
            >
              {project.projectName}
            </LongText>
          </ProjectNameWrapper>
        </WithAvatarSpace>
      ),
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) =>
      sortByText(prevProject.projectName, nextProject.projectName),
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: <FormattedMessage id="projects.list.project_owner" />,
    key: 'projectOwner',
    width: 200,
    ellipsis: true,
    render: (_text, project: ArchivedProject) =>
      project.projectOwner && (
        <UserTag
          color={getRandomColor(project.projectOwner.userId)}
          imageUrl={project.projectOwner.photoUrl}
          label={project.projectOwner.firstName}
        />
      ),
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) =>
      sortByText(
        fullName(prevProject.projectOwner),
        fullName(nextProject.projectOwner),
      ),
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: <FormattedMessage id="status" />,
    key: 'status',
    width: 200,
    dataIndex: ['status'],
    render: (_text, project: ArchivedProject) => {
      const status = getProjectStatusDetails(project.fields);

      return status ? renderStatusField(status.value, status) : '';
    },
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) => {
      const prevProjStatus = getProjectStatusDetails(prevProject.fields);
      const nextProjStatus = getProjectStatusDetails(nextProject.fields);

      return sortByText(prevProjStatus.label, nextProjStatus.label);
    },
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: <FormattedMessage id="projects.list.start_date" />,
    key: 'startDate',
    width: 200,
    render: (_text, project: ArchivedProject) =>
      project.startDate && (
        <HumanisedDate timestamp={moment(project.startDate)} />
      ),
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) =>
      sortByDates(prevProject.startDate, nextProject.startDate),
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: <FormattedMessage id="projects.list.due_date" />,
    key: 'dueDate',
    width: 200,
    render: (_text, project: ArchivedProject) =>
      project.dueDate && <HumanisedDate timestamp={moment(project.dueDate)} />,
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) =>
      sortByDates(prevProject.dueDate, nextProject.dueDate),
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: <FormattedMessage id="project.archived_by" />,
    key: 'archivedBy',
    width: 200,
    render: (_text, project: ArchivedProject) => (
      <UserTag
        color={getRandomColor(project.archivedBy?.userId)}
        imageUrl={project.archivedBy?.photoUrl}
        label={project.archivedBy?.firstName}
      />
    ),
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) =>
      sortByText(
        fullName(prevProject.archivedBy),
        fullName(nextProject.archivedBy),
      ),
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: <FormattedMessage id="project.archived_date" />,
    key: 'archivedOn',
    render: (_text, project: ArchivedProject) => (
      <HumanisedDate timestamp={moment(project.archivedOn)} />
    ),
    sorter: (prevProject: ArchivedProject, nextProject: ArchivedProject) => {
      return sortByDates(
        moment(prevProject.archivedOn, 'dd/mm/yy').toString(),
        moment(nextProject.archivedOn, 'dd/mm/yy').toString(),
      );
    },
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  },
  {
    titleText: null,
    key: 'more_actions',
    width: 40,
    fixed: 'right' as const,
    ellipsis: true,
    render: (_text, project: ArchivedProject) => (
      <MoreActions data-test-id={`project-${project.projectId}-more-actions`}>
        <MoreActionsMenu project={project} />
      </MoreActions>
    ),
  },
].map((x) => ({
  showSorterTooltip: false,
  width: COLUMN_HEADER_WIDTH,
  title: <ColumnHeader title={x.titleText} />,
  ...x,
}));
